import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import '../../../shared/StyledInput.css';
import './SupplierRebateRuleForm.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import DateInput from '../../../shared/DateInput';
import NumberInput from '../../../shared/NumberInput';
import brandLabel from '../../../shared/brandLabel';

// The whole shape of a Supplier Rebate deal — Supplier + Brand + Category
// (includes sub-categories) + UoM1/UoM2 (the pair Discount Given is
// measured between) + Customer Price Group (the Dealer Price basis) +
// Rebate Rate (% of Discount Given) + an optional Start/End Date window.
// Absorbs what a separate Assignment used to do — see
// docs/product/modules/Purchasing/vis_supplier_rebates.md. `initial` doubles
// as the pre-fill for both Edit and Duplicate (Duplicate just omits `id`).
function SupplierRebateRuleForm({ initial, onSave, onCancel, loading }) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  // Reads off commercial_entity_id (issue #200, was supplier_id) — the
  // model's own renamed Supplier column; submitted back as supplier_id
  // below, the request body's public field name (see
  // supplierRebateRuleController.js's resolveFields).
  const [supplierId, setSupplierId] = useState(initial?.commercial_entity_id ? String(initial.commercial_entity_id) : '');
  const [brandId, setBrandId] = useState(initial?.brand_id ? String(initial.brand_id) : '');
  const [categoryId, setCategoryId] = useState(initial?.product_category_id ? String(initial.product_category_id) : '');
  const [uom1Id, setUom1Id] = useState(initial?.uom1_id ? String(initial.uom1_id) : '');
  const [uom2Id, setUom2Id] = useState(initial?.uom2_id ? String(initial.uom2_id) : '');
  const [customerPriceGroupId, setCustomerPriceGroupId] = useState(initial?.customer_price_group_id ? String(initial.customer_price_group_id) : '');
  const [rebateRate, setRebateRate] = useState(initial?.rebate_rate !== undefined && initial?.rebate_rate !== null ? String(Number(initial.rebate_rate)) : '');
  const [startDate, setStartDate] = useState(initial?.start_date || '');
  const [endDate, setEndDate] = useState(initial?.end_date || '');
  const [error, setError] = useState('');

  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [scopeUoms, setScopeUoms] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const skipNextScopeClear = useRef(true);

  useEffect(() => {
    axios.get('/api/suppliers').then((res) => setSuppliers((res.data.data || []).filter((s) => s.is_active))).catch(() => setSuppliers([]));
    axios.get('/api/brands').then((res) => setBrands((res.data.data || []).filter((b) => b.is_active))).catch(() => setBrands([]));
    axios.get('/api/product-categories').then((res) => setCategories((res.data.data || []).filter((c) => c.is_active))).catch(() => setCategories([]));
    axios.get('/api/uoms', { params: { status: 'active' } }).then((res) => setUoms(res.data.data || [])).catch(() => setUoms([]));
    axios.get('/api/customer-price-groups').then((res) => setCustomerGroups((res.data.data || []).filter((g) => g.is_active))).catch(() => setCustomerGroups([]));
  }, []);

  // Which UoMs the currently-selected Brand+Category scope's SKUs actually
  // have — narrows UoM1/UoM2 to real options instead of every UoM the
  // tenant has ever defined. Falls back to the full list (below) until both
  // Brand and Category are picked, or if the scope resolves to no SKUs yet.
  useEffect(() => {
    if (!brandId || !categoryId) {
      setScopeUoms([]);
      return;
    }
    axios.get('/api/supplier-rebate-rules/scope-uoms', { params: { brand_id: brandId, product_category_id: categoryId } })
      .then((res) => setScopeUoms(res.data.data || []))
      .catch(() => setScopeUoms([]));
  }, [brandId, categoryId]);

  // If Brand/Category changes after UoM1/UoM2 were already picked (not on
  // the initial Edit/Duplicate prefill) and the new scope no longer
  // includes them, clear them rather than silently keeping an invalid
  // combination selected.
  useEffect(() => {
    if (skipNextScopeClear.current) {
      skipNextScopeClear.current = false;
      return;
    }
    if (scopeUoms.length === 0) return;
    const validIds = new Set(scopeUoms.map((u) => String(u.id)));
    if (uom1Id && !validIds.has(uom1Id)) setUom1Id('');
    if (uom2Id && !validIds.has(uom2Id)) setUom2Id('');
    // eslint-disable-next-line
  }, [scopeUoms]);

  const uomOptions = scopeUoms.length > 0 ? scopeUoms : uoms;

  const validate = () => {
    if (!name.trim()) return 'Name is required.';
    if (!supplierId) return 'Supplier is required.';
    if (!brandId) return 'Brand is required.';
    if (!categoryId) return 'Category is required.';
    if (!uom1Id || !uom2Id) return 'UoM1 and UoM2 are required.';
    if (uom1Id === uom2Id) return 'UoM1 and UoM2 must be different.';
    if (!customerPriceGroupId) return 'Customer Price Group is required, to price the Discount Given basis against.';
    const parsedRate = Number(rebateRate);
    if (rebateRate === '' || Number.isNaN(parsedRate) || parsedRate <= 0) return 'Rebate Rate is required and must be greater than zero.';
    if (startDate && endDate && endDate < startDate) return 'End Date cannot be before Start Date.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');

    onSave({
      name: name.trim(),
      description: description.trim(),
      supplier_id: supplierId,
      brand_id: brandId,
      product_category_id: categoryId,
      uom1_id: uom1Id,
      uom2_id: uom2Id,
      customer_price_group_id: customerPriceGroupId,
      rebate_rate: Number(rebateRate),
      start_date: startDate || null,
      end_date: endDate || null,
    });
  };

  const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };
  const sectionStyle = { marginTop: 14, padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', maxWidth: 640 };

  return (
    <form className="supplier-rebate-rule-form" onSubmit={handleSubmit} style={{ minWidth: 320, maxWidth: 640 }}>
      <div style={{ ...sectionStyle, marginTop: 0 }}>
        <h4 style={sectionHeaderStyle}>Deal</h4>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label>Name<RequiredMark /></label><br />
            <input
              className="styled-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="e.g., SP Cables Microphone Cables"
              style={{ width: 260 }}
            />
          </div>
          <div>
            <label>Supplier<RequiredMark /></label><br />
            <select className="styled-input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} style={{ width: 200 }}>
              <option value="">Select a Supplier…</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <label>Description</label><br />
          <textarea
            className="styled-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            style={{ width: '100%', maxWidth: 480, resize: 'vertical' }}
            placeholder="Optional notes about this deal"
          />
        </div>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Scope</h4>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label>Brand<RequiredMark /></label><br />
            <select className="styled-input" value={brandId} onChange={(e) => setBrandId(e.target.value)} style={{ width: 200 }}>
              <option value="">Select a Brand…</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{brandLabel(b)}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Category<RequiredMark /></label><br />
            <select className="styled-input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} style={{ width: 220 }}>
              <option value="">Select a Category…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{'   '.repeat(c.depth || 0)}{c.name}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Includes every sub-category beneath it.</div>
          </div>
        </div>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Formula</h4>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label>UoM1<RequiredMark /></label><br />
            <select className="styled-input" value={uom1Id} onChange={(e) => setUom1Id(e.target.value)} style={{ width: 140 }}>
              <option value="">Select…</option>
              {uomOptions.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>UoM2<RequiredMark /></label><br />
            <select className="styled-input" value={uom2Id} onChange={(e) => setUom2Id(e.target.value)} style={{ width: 140 }}>
              <option value="">Select…</option>
              {uomOptions.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Rebate Rate (%)<RequiredMark /></label><br />
            <NumberInput
              maxDecimals={4}
              step="0.01"
              value={rebateRate}
              onChange={(e) => setRebateRate(e.target.value)}
              style={{ width: 100 }}
            />
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
          UoM1 is the loose/reference unit, UoM2 is the unit that earns the rebate (e.g. UoM1 = PCS, UoM2 = BX) — Discount Given is UoM1's Dealer Price vs UoM2's, both per Base Unit.
          {' '}{scopeUoms.length > 0 ? 'Showing only UoMs used by SKUs in this Brand + Category.' : 'Pick a Brand and Category above to narrow this list to what those SKUs actually use.'}
        </div>

        <div style={{ marginTop: 14 }}>
          <label>Customer Price Group<RequiredMark /></label><br />
          <select className="styled-input" value={customerPriceGroupId} onChange={(e) => setCustomerPriceGroupId(e.target.value)} style={{ width: 220 }}>
            <option value="">Select a group…</option>
            {customerGroups.map((g) => (
              <option key={g.id} value={g.id}>{'   '.repeat(g.depth || 0)}{g.name}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Which Customer Price Group's Adjusted Price is used as the basis for the Discount Given calc.</div>
        </div>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Validity</h4>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label>Start Date</label><br />
            <DateInput className="styled-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label>End Date (expiry)</label><br />
            <DateInput className="styled-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 14 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default SupplierRebateRuleForm;
